import { useGetOrganizationSettings } from "@fp/admin-api-client/organizations/organizations.client";
import { Badge } from "@fp/ui/components/base/badges/badges";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { versionLabel } from "@fp/ui/env";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ChevronRight } from "@untitledui/icons";
import { useState } from "react";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { ComingSoonFeedbackModal } from "@/components/coming-soon-feedback-modal";

export const Route = createFileRoute("/settings/")({
  component: RouteComponent,
});

const settingsOptions = [
  {
    id: "tags",
    title: "Tags Management",
    href: "/settings/tags",
  },
  {
    id: "1099-filing",
    title: "1099 Filing",
    href: "/settings/tax-forms",
    comingSoon: true,
  },
  {
    id: "staff",
    title: "Staff Members",
    href: "/staff",
  },
  {
    id: "notifications",
    title: "Notifications",
    href: "/settings/notifications",
  },
  {
    id: "payout-policies",
    title: "Payout Policies",
    href: "/settings/payout-policies",
  },
  {
    id: "roles-permissions",
    title: "Roles & Permissions",
    href: "/settings/roles",
    comingSoon: true,
  },
];

function RouteComponent() {
  const router = useRouter();
  const { data: orgSettingsResponse } = useGetOrganizationSettings();
  const [comingSoonFeature, setComingSoonFeature] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const visibleOptions = settingsOptions.filter(
    (option) => option.id !== "payout-policies" || orgSettingsResponse?.data.payoutPolicyEnabled,
  );

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Settings"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer className="space-y-0 bg-white p-0">
        <div className="divide-y divide-gray-200">
          {visibleOptions.map((option) => (
            <button
              type="button"
              key={option.id}
              onClick={() =>
                option.comingSoon
                  ? setComingSoonFeature({
                      id: option.id,
                      title: option.title,
                    })
                  : router.navigate({ to: option.href })
              }
              className="w-full flex items-center justify-between px-4 py-6 hover:bg-gray-50 transition-colors text-left first:border-t first:border-gray-200 last:border-b last:border-gray-200"
            >
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <ChevronRight className="h-6 w-6 text-gray-600" />
                </div>
                <span className="text-lg font-normal text-gray-900">
                  {option.title}
                </span>
              </div>
              {option.comingSoon && (
                <Badge size="sm" color="gray">
                  Coming Soon
                </Badge>
              )}
            </button>
          ))}
        </div>
      </PageContentContainer>
      <div className="mt-8 text-center">
        <span className="text-xs text-gray-400">{versionLabel}</span>
      </div>
      <ComingSoonFeedbackModal
        isOpen={comingSoonFeature !== null}
        onClose={() => setComingSoonFeature(null)}
        feature={comingSoonFeature?.id ?? ""}
        featureLabel={comingSoonFeature?.title ?? ""}
      />
    </AdminLayout>
  );
}
